import React, { FC, useEffect } from 'react'
import { hot } from 'react-hot-loader/root'
import { IntercomProvider } from 'react-use-intercom'
import { ThemeProvider } from 'styled-components'
import { ParallaxProvider } from 'react-scroll-parallax'
import { useDispatch, useSelector } from 'react-redux'
import { useCookies } from 'react-cookie'
import { QueryClient, QueryClientProvider } from 'react-query'
import { withLDProvider } from 'launchdarkly-react-client-sdk'

import { identifySegment } from '@app/utils/analytics'
import theme from '@app/ui/theme'
import GlobalStyles from '@app/ui/theme/globalStyles'
import { Meta } from '@ui/blocks/Meta'
import { NotificationsContainer } from '@ui/blocks/Notifications'
import { CTMScript } from '@ui/blocks/CTMScript'
import { getSessionIdSelector } from '@app/store/session'
import { runtimeConfig } from '@app/common/config'
import { useMarketingAttribution } from '@app/hooks/useMarketingAttribution'
import { useURLReflectionCookie } from './hooks/useURLReflectionCookie'
import { useLDGroups } from './hooks/useLDGroups'

import { appRoutes } from './common/routes'
import { renderRouting } from './common/routing'
import { fillStoreInitBookingFlow } from '../server/lib/utils/fillStore/fillStoreInitBookingFlow'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 30000 // 30 seconds
    }
  }
})

const Pages: FC = ({ children }) => {
  const sessionId = useSelector(getSessionIdSelector)

  const [cookies, setCookie, removeCookie] = useCookies([])
  const dispatch = useDispatch()
  useLDGroups()
  useEffect(() => {
    if (sessionId) {
      identifySegment(sessionId)
    }
  }, [sessionId])

  // initialize
  useEffect(() => {
    const actions = fillStoreInitBookingFlow(window.location.href, window.location.hostname, runtimeConfig, {
      get: name => cookies[name],
      set: setCookie,
      remove: removeCookie
    })

    actions.forEach(action =>
      dispatch(
        action({
          dispatch,
          url: window.location.hostname + window.location.search
        })
      )
    )
  }, [cookies, setCookie, removeCookie, dispatch])

  return <>{children}</>
}

const launchDarklyConfig = {
  clientSideID: runtimeConfig.LAUNCH_DARKLY_CLIENT_ID || '',
  user: {
    key: 'none'
  },
  options: {}
}

export const App = hot(
  withLDProvider(launchDarklyConfig)(() => {
    useMarketingAttribution()
    useURLReflectionCookie()
    const intercomAppId = runtimeConfig.joinSproutTherapyConfig?.INTERCOM_ID || ''
    const googleTagManagerId = runtimeConfig.joinSproutTherapyConfig?.GTM_ID || ''

    return (
      <ThemeProvider theme={theme}>
        <QueryClientProvider client={queryClient}>
          <IntercomProvider appId={intercomAppId} autoBoot initializeDelay={5000}>
            <Meta
              title="Elemy | Pediatric Behavioral Health & Autism Care for All"
              description="Every child is different. At Elemy, we believe care should never be one-size-fits-all. Chat with us about in-home, personalized care for your child."
            />
            <noscript>
              <iframe
                title="Google Tag Manager (noscript)"
                src={`https://www.googletagmanager.com/ns.html?id=${googleTagManagerId}`}
                height="0"
                width="0"
                style={{ display: 'none', visibility: 'hidden' }}
              />
            </noscript>

            <GlobalStyles />
            <CTMScript />
            <ParallaxProvider>
              <Pages>{renderRouting(appRoutes)}</Pages>
            </ParallaxProvider>
            <NotificationsContainer />
          </IntercomProvider>
        </QueryClientProvider>
      </ThemeProvider>
    )
  })
)
